import { WorkflowMessagesService } from '../services/workflow-messages.service';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { ROUTER_NAVIGATED } from '@ngrx/router-store';
import { Action, ActionCreator, Creator, Store } from '@ngrx/store';
import { addDays, endOfYear, format } from 'date-fns';
import {
  castArray,
  compact,
  defaultsDeep,
  filter as _filter,
  find,
  findLast,
  get,
  head,
  includes,
  isEmpty,
  isNil,
  map as _map,
  reject,
  some,
  toInteger,
  toNumber,
} from 'lodash';
import {
  catchError,
  combineLatest,
  filter,
  map,
  merge,
  Observable,
  of,
  switchMap,
  takeUntil,
  tap,
  throwError,
} from 'rxjs';

import {
  ClientActions,
  ClientEnquiriesActions,
} from '@lockton-apps/clients/actions';
import {
  DashboardBrokerActions,
  DashboardBrokerLsosActions,
  DashboardContactActions,
} from '@lockton-apps/dashboard/action';
import {
  EnquiryConfigurationActions,
  EnquiryOverviewActions,
  EnquiryOverviewActionTypes,
  NewClientEnquiryActions,
  NewEnquiryActions,
  UpdateEnquiryActions,
} from '@lockton-apps/enquiries/action';
import {
  LsosRenewalsApplicationActions,
  LsosRenewalsOverviewActions,
} from '@lockton-apps/lsos-renewals/action';
import {
  InvoiceSummaryActions,
  NewPaymentsActions,
  PaymentsCardCompleteActions,
} from '@lockton-apps/payments/action';
import { PolicyDetailsActions } from '@lockton-apps/policies/action';
import {
  ProposalShellActions,
  ProposalStepActions,
} from '@lockton-apps/proposals/action';
import {
  QuoteAddonsActions,
  QuoteConfigurationEndorsementsActions,
  QuoteDetailsActions,
  QuoteEndorsementsActions,
  QuoteOptionsActions,
  QuoteOverviewActions,
  QuoteSummaryActions,
} from '@lockton-apps/quotes/action';
import { EndorsementsSharedActions } from '@lockton-apps/shared/action/endorsements';
import { PoliciesApiActions } from '@lockton-apps/shared/action/policies';
import {
  QuotesApiActions,
  QuotesApiActionTypes,
} from '@lockton-apps/shared/action/quotes';
import { AuthSelectors } from '@lockton-apps/shared/data-access/auth';
import { SharedClientsSelectors } from '@lockton-apps/shared/data-access/clients';
import { PoliciesSelectors } from '@lockton-apps/shared/data-access/policies';
import { PortalConfigSelectors } from '@lockton-apps/shared/data-access/portal-config';
import {
  ClientRouterSelectors,
  QuoteRouterSelectors,
  selectQueryParam,
  selectRouteParam,
} from '@lockton-apps/shared/data-access/router';
import {
  ApplicationConfig,
  Client,
  EnquiryConfig,
  EnquiryConfigUpdate,
  Partners,
  PatchQuoteResponse,
  Proposal,
  Quote,
  QUOTE_RESPONSE_ACTIONS,
  QUOTE_STATUSES,
  QUOTE_TYPES,
  QuoteType,
  Role,
} from '@lockton-apps/shared/interfaces';
import {
  ConfirmationDialogComponent,
  ConfirmDialogData,
  FeedbackService,
} from '@lockton-apps/shared/ui/lui';
import { PolicyCobMap } from '@lockton-apps/shared/util/policy-classification';
import { RenewalNotificationsService } from '@lockton-apps/shared/util/renewal-notifications';

import { MatDialog } from '@angular/material/dialog';
import { AuthUserSelectors } from '@lockton-apps/shared/data-access/auth-user-selectors';
import {
  getProposalUrl,
  ProposalBackupService,
} from '@lockton-apps/shared/util/proposal-utils';
import { SharedQuotesSelectors } from '../..';
import {
  DISPATCH_ACTIONS,
  filterRegisteredApiActions,
} from '../utils/patch-actions';
import { QuotesService } from '../services/quotes.service';
import { Quotes2Service } from '../services/quotes2.service';
import { selectLatestQuote, selectRoutedQuote } from './quotes.selectors';
import { LocalActions } from './actions';
import { ProposalConfigSelectors } from '@lockton-apps/shared/data-access/proposals';
import {
  Connection,
  getDevConfig,
  isV2,
} from '@lockton-apps/shared/util/helpers';
import { ProposalsLocalActions } from '@lockton-apps/shared/action/proposals';
import { DateFnsHelpersService } from '@lockton-apps/shared/util/ngx-date-fns-tz';

const MESSAGES = {
  genericFailure: $localize`Something has gone wrong. Please try again`,
  endorsementSuccess: $localize`Health evaluation submitted successfully`,
  endorsementFailure: $localize`Failed to submit health evaluation`,
  releasedSuccess: $localize`Proposal successfully released to Client`,
  brokerAssignedSuccess: $localize`Successfully assigned broker to the renewal`,
  approvedSuccess: $localize`Quote was successfully approved`,
  completeSuccess: $localize`Quote successfully completed`,
  healthEvaluationRequired: $localize`Quote requires health evaluation`,
  tierSelected: $localize`Option successfully selected`,
  tierAdjusted: $localize`Option successfully adjusted`,
  adjustmentApplied: $localize`Adjustment successfully applied`,
  addonAdjusted: $localize`Addon successfully adjusted`,
  addonEnabled: $localize`Addon successfully added to quote`,
  addonDisabled: $localize`Addon successfully removed from quote`,
  statusUpdated: $localize`Status successfully updated`,
  quoteHoldSuccess: $localize`Quote successfully put on hold`,
  quoteAcceptedSuccess: $localize`Quote successfully accepted`,
  quoteInvoiceSuccess: $localize`Invoice successfully generated`,
};

type SaveProposalActionCreator = ActionCreator<
  string,
  Creator<any, { proposal: Proposal }>
>;

interface PatchQuoteActionCreatorProps extends Action {
  quote: Quote;
  [key: string]: any;
}
type PatchQuoteActionCreator = ActionCreator<
  string,
  (props: PatchQuoteActionCreatorProps) => PatchQuoteActionCreatorProps
>;

const MESSAGES2: Record<
  string,
  {
    text: string;
    type: 'info' | 'success' | 'error';
  }
> = {
  [QuotesApiActionTypes.EndorsementSubmittedSuccess]: {
    text: MESSAGES.endorsementSuccess,
    type: 'success',
  },
  [QuotesApiActionTypes.EndorsementSubmittedFailure]: {
    text: MESSAGES.endorsementFailure,
    type: 'error',
  },
  [QuotesApiActionTypes.QuoteReleasedSuccess]: {
    text: MESSAGES.releasedSuccess,
    type: 'success',
  },
  [QuotesApiActionTypes.BrokerAssignedSuccess]: {
    text: MESSAGES.brokerAssignedSuccess,
    type: 'success',
  },
  [QuotesApiActionTypes.QuoteApprovedSuccess]: {
    text: MESSAGES.approvedSuccess,
    type: 'success',
  },
  [QuotesApiActionTypes.CompleteSuccess]: {
    text: MESSAGES.completeSuccess,
    type: 'success',
  },
  [QuotesApiActionTypes.TierSelectedSuccess]: {
    text: MESSAGES.tierSelected,
    type: 'success',
  },
  [QuotesApiActionTypes.TierAdjustedSuccess]: {
    text: MESSAGES.tierAdjusted,
    type: 'success',
  },
  [QuotesApiActionTypes.AdjustmentsAppliedSuccess]: {
    text: MESSAGES.adjustmentApplied,
    type: 'success',
  },
  [QuotesApiActionTypes.AddonAdjustedSuccess]: {
    text: MESSAGES.addonAdjusted,
    type: 'success',
  },
  [QuotesApiActionTypes.UpdateStatusSuccess]: {
    text: MESSAGES.statusUpdated,
    type: 'success',
  },
  [QuotesApiActionTypes.HoldSuccess]: {
    text: MESSAGES.quoteHoldSuccess,
    type: 'success',
  },
  [QuotesApiActionTypes.AcceptSuccess]: {
    text: MESSAGES.quoteAcceptedSuccess,
    type: 'success',
  },
  [QuotesApiActionTypes.InvoiceSuccess]: {
    text: MESSAGES.quoteInvoiceSuccess,
    type: 'success',
  },
  [QuotesApiActionTypes.SendRenewalInviteSuccess]: {
    text: $localize`Renewal invite successfully sent`,
    type: 'success',
  },
  [QuotesApiActionTypes.PaymentReceivedSuccess]: {
    text: $localize`Successfully updated`,
    type: 'success',
  },
  [QuotesApiActionTypes.ProposalReturnedSuccess]: {
    text: $localize`Successfully returned proposal to client`,
    type: 'success',
  },
  [QuotesApiActionTypes.Reconfigured2Success]: {
    text: $localize`Proposal successfully reconfigured`,
    type: 'success',
  },
  [QuotesApiActionTypes.SendToClientSuccess]: {
    text: $localize`Quote successfully sent to client`,
    type: 'success',
  },
  [QuotesApiActionTypes.RequotedSuccess]: {
    text: $localize`Quote successfully requoted`,
    type: 'success',
  },
};

const CONFIRMATION_DIALOG_EXCEPTIONS: [Partners, string][] = [
  [Partners.LSOS_ADDON, EnquiryOverviewActionTypes.SendRenewalInvite],
  [Partners.LSOS_ADDON, EnquiryOverviewActionTypes.SendToClient],
];

interface Handler {
  message?: {
    text: string;
    type: 'success' | 'info' | 'error';
  };
  redirect?: (res: object) => { path: Array<string | number> };
}

const handlers: Record<string, Handler> = {
  [QuotesApiActionTypes.ChecklistUpdatedSuccess]: {
    message: {
      text: $localize`Checklist successfully updated`,
      type: 'success',
    },
  },
  [QuotesApiActionTypes.ReconfiguredSuccess]: {
    message: {
      text: $localize`Quote successfully reconfigured`,
      type: 'success',
    },
    redirect: (res) => ({
      path: ['/clients', get(res, 'quotes[0].clientId', ''), 'enquiries'],
    }),
  },
  [QuotesApiActionTypes.ReconfiguredFailure]: {
    message: {
      text: $localize`Quote could not be reconfigured at this time`,
      type: 'error',
    },
  },
};

@Injectable()
export class SharedQuotesEffects {
  private navigated$ = this.actions$.pipe(ofType(ROUTER_NAVIGATED));

  loadQuotes$ = createEffect(() =>
    merge(
      this.actions$.pipe(
        ofType(QuoteOverviewActions.init, InvoiceSummaryActions.init)
      ),
      this.actions$.pipe(
        ofType(DashboardContactActions.init, NewClientEnquiryActions.init),
        concatLatestFrom(() =>
          this.store.select(PortalConfigSelectors.selectActivePortalId)
        ),
        filter(([, portalId]) => portalId !== Partners.LSOS)
      )
    ).pipe(
      switchMap(() =>
        this.quotesService2.getQuotesWithProp().pipe(
          map((quotes) => QuotesApiActions.loadQuotesSuccess({ quotes })),
          catchError((error) =>
            of(QuotesApiActions.loadQuotesFailure({ error }))
          )
        )
      )
    )
  );

  enquiriesInitAndQuoteLoaded$ = this.actions$.pipe(
    ofType(EnquiryOverviewActions.init),
    switchMap(() =>
      this.actions$.pipe(
        ofType(QuotesApiActions.loadQuoteSuccess),
        map(({ quote }) => quote.clientId),
        takeUntil(this.navigated$)
      )
    )
  );

  loadClientQuotes$ = createEffect(() =>
    merge(
      this.enquiriesInitAndQuoteLoaded$,
      this.actions$.pipe(
        ofType(
          ClientEnquiriesActions.init,
          ClientActions.init,
          ClientActions.clientSelected
        ),
        concatLatestFrom(() =>
          this.store.select(ClientRouterSelectors.selectRoutedId)
        ),
        map(([, clientId]) => clientId),
        filter(Boolean)
      ),
      this.actions$.pipe(
        ofType(ProposalShellActions.init),
        filter(() => !getDevConfig('proposalForm.runLocally')),
        concatLatestFrom(() =>
          this.store.select(ClientRouterSelectors.selectRoutedId)
        ),
        map(([, clientId]) => clientId),
        filter(Boolean)
      ),
      this.actions$.pipe(
        ofType(NewEnquiryActions.init),
        concatLatestFrom(() =>
          this.store.select(ClientRouterSelectors.selectQueriedId)
        ),
        map(([, clientId]) => clientId),
        filter(Boolean)
      ),
      this.actions$.pipe(
        ofType(UpdateEnquiryActions.init),
        switchMap(() =>
          this.actions$.pipe(ofType(QuotesApiActions.loadQuoteSuccess)).pipe(
            map(({ quote }) => quote.clientId),
            takeUntil(this.navigated$)
          )
        )
      ),
      this.actions$.pipe(
        ofType(PolicyDetailsActions.init),
        switchMap(() =>
          this.actions$
            .pipe(ofType(PoliciesApiActions.loadPolicyForPolicyDetailsSuccess))
            .pipe(
              map(({ policy }) => policy.policyClients[0].id),
              takeUntil(this.navigated$)
            )
        )
      )
    ).pipe(
      switchMap((clientId) =>
        this.quotesService2
          .getQuotesByClientId(clientId)
          .pipe(
            map((quotes) =>
              QuotesApiActions.loadClientQuotesSuccess({ quotes })
            )
          )
      )
    )
  );

  loadQuote$ = createEffect(() =>
    merge(
      this.actions$.pipe(
        ofType(
          QuoteEndorsementsActions.init,
          EnquiryOverviewActions.init,
          UpdateEnquiryActions.init,
          QuoteOptionsActions.init,
          QuoteAddonsActions.init,
          QuoteSummaryActions.init,
          QuotesApiActions.tierSelectedSuccess,
          QuotesApiActions.addonToggledSuccess,
          QuotesApiActions.addonAdjustedSuccess,
          NewPaymentsActions.init,
          PaymentsCardCompleteActions.init,
          EnquiryConfigurationActions.init,
          QuoteDetailsActions.init,
          QuoteConfigurationEndorsementsActions.init
        )
      ),
      this.actions$.pipe(
        ofType(QuotesApiActions.tierAdjustedSuccess),
        filter(({ option }) => option.selected)
      )
    ).pipe(
      concatLatestFrom(() =>
        this.store.select(QuoteRouterSelectors.selectRoutedId)
      ),
      switchMap(([, quoteId]) => {
        if (!quoteId) {
          return throwError(() => new Error('No quote Id in url'));
        }
        return this.quotesService2.getQuote(quoteId).pipe(
          map((quote) => QuotesApiActions.loadQuoteSuccess({ quote })),
          catchError(({ error }) =>
            of(QuotesApiActions.loadQuoteFailure(error))
          )
        );
      }),
      catchError((error) => {
        return of(QuotesApiActions.loadQuoteFailure(error));
      })
    )
  );

  loadQuoteByProposalId$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProposalsLocalActions.getConfigSuccess),
      concatLatestFrom(() => [
        this.store.select(selectRouteParam('proposalId')),
        this.store.select(selectRouteParam('portalId')).pipe(map(toNumber)),
        this.store.select(selectRouteParam('configId')).pipe(map(toNumber)),
        this.store.select(selectRouteParam('clientId')).pipe(map(toNumber)),
      ]),
      switchMap(([, proposalId, portalId, configId, clientId]) => {
        if (!proposalId) {
          return throwError(() => new Error('No proposal Id in url'));
        }

        return (
          isV2(toInteger(portalId)) ? this.quotesService2 : this.quotesService
        )
          .getQuoteByProposalId(proposalId, portalId, configId, clientId)
          .pipe(
            map((quote) => QuotesApiActions.loadQuoteSuccess({ quote })),
            catchError(({ error }) =>
              of(QuotesApiActions.loadQuoteFailure(error))
            )
          );
      })
    )
  );

  initialiseQuote$ = createEffect(() =>
    merge(
      this.actions$.pipe(
        ofType(NewEnquiryActions.formSubmitted),
        concatLatestFrom(() => [
          this.store
            .select(SharedClientsSelectors.selectQueriedClient)
            .pipe(filter(Boolean)),
          this.store.select(selectQueryParam('type')).pipe(filter(Boolean)),
          this.store.select(ProposalConfigSelectors.selectAll),
        ]),
        switchMap(([{ enquiryConfiguration }, client, type, configs]) => {
          const portalId = enquiryConfiguration.portalId
            ? parseInt(enquiryConfiguration.portalId)
            : client.portalId;

          const result = {
            ...enquiryConfiguration,
            clientId: client.id,
            portalId,
            type: type as QuoteType,
            selectedProducts: _map(
              enquiryConfiguration.selectedProducts,
              (cob) => ({
                ...cob,
                inceptionDate: this.dateFnsHelperService.toNeutralDate(
                  cob.inceptionDate
                ),
                expiryDate: this.dateFnsHelperService.toNeutralDate(
                  cob.expiryDate
                ),
                renewalDate: this.dateFnsHelperService.toNeutralDate(
                  cob.renewalDate
                ),
              })
            ),
          };
          const config = find(configs, {
            id: `${client.portalId}-${enquiryConfiguration.selectedProducts[0].subType}`,
          });

          return this.newEnquiryConfirmation$(result, client, config);
        })
      ),
      this.actions$.pipe(
        ofType(NewClientEnquiryActions.formSubmitted),
        concatLatestFrom(() => [
          this.store
            .select(
              AuthUserSelectors.selectAuthenticatedContactFirstPrimaryClient
            )
            .pipe(filter(Boolean)),
          this.store
            .select(PortalConfigSelectors.selectActivePortalId)
            .pipe(filter(Boolean)),
        ]),
        // TODO: move to a service / util
        map(([{ selectedProducts }, client, portalId]) => ({
          selectedProducts: _map(selectedProducts, (product) => ({
            id: product,
            inceptionDate: format(addDays(new Date(), 1), 'yyyy-MM-dd'),
            expiryDate: format(endOfYear(new Date()), 'yyyy-MM-dd'),
            renewalDate: format(
              addDays(endOfYear(new Date()), 1),
              'yyyy-MM-dd'
            ),
          })),
          clientId: client.id,
          portalId: portalId,
          type: QUOTE_TYPES.NewBusiness,
        }))
      )
    ).pipe(
      concatLatestFrom(() =>
        this.store.select(PortalConfigSelectors.selectPortalConfigDefinition)
      ),
      map(([config, portalConfigDef]) => {
        const partnerConfig = portalConfigDef[config.portalId];
        const usesLegacyQuotesApi = !!partnerConfig?.other?.usesLegacyQuotesApi;
        return { config, usesLegacyQuotesApi };
      }),
      switchMap(({ config, usesLegacyQuotesApi }) => {
        const service = usesLegacyQuotesApi
          ? this.quotesService
          : this.quotesService2;
        return service.initialise(config).pipe(
          map((quotes) => QuotesApiActions.initialisedSuccess({ quotes })),
          catchError((error) =>
            of(QuotesApiActions.initialisedFailure({ error }))
          )
        );
      })
    )
  );

  /** @deprecated - Used for legacy partners */
  legacyReconfigureQuote$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UpdateEnquiryActions.formSubmitted),
      concatLatestFrom(() => [
        this.store.select(selectRoutedQuote).pipe(filter(Boolean)),
        this.store.select(PortalConfigSelectors.selectPortalConfigDefinition),
      ]),
      map(([{ enquiryConfiguration }, quote, portalConfigDef]) => {
        const partnerConfig = portalConfigDef[quote.portalId];
        const usesLegacyQuotesApi = !!partnerConfig?.other?.usesLegacyQuotesApi;
        return {
          enquiryConfiguration: { ...enquiryConfiguration, quoteId: quote.id },
          usesLegacyQuotesApi,
        };
      }),
      filter(({ usesLegacyQuotesApi }) => usesLegacyQuotesApi),
      switchMap(({ enquiryConfiguration }) =>
        this.quotesService.reconfigure(enquiryConfiguration).pipe(
          map((quotes) => QuotesApiActions.reconfiguredSuccess({ quotes })),
          catchError((error) =>
            of(QuotesApiActions.reconfiguredFailure({ error }))
          )
        )
      )
    )
  );

  approveQuote$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProposalStepActions.quoteApproved),
      switchMap(({ quoteId, comment }) => {
        return this.quotesService.approve(quoteId, comment).pipe(
          map((quote) => QuotesApiActions.quoteApprovedSuccess({ quote })),
          catchError((error) =>
            of(QuotesApiActions.quoteApprovedFailure({ error }))
          )
        );
      })
    )
  );

  /** @depractated use returnProposal */
  releaseQuote$ = createEffect(() =>
    merge(
      this.actions$.pipe(ofType(ProposalStepActions.quoteReleased)),
      this.actions$.pipe(
        ofType(ProposalStepActions.proposalReturned),
        concatLatestFrom(() =>
          this.store.select(PortalConfigSelectors.selectPortalConfigDefinition)
        ),
        map(([{ quote }, portalConfigDef]) => ({
          quote,
          usesLegacyQuotesApi:
            !!portalConfigDef[quote.portalId]?.other?.usesLegacyQuotesApi,
        })),
        filter(({ usesLegacyQuotesApi }) => usesLegacyQuotesApi),
        map(({ quote }) => ({ quoteId: quote.id }))
      )
    ).pipe(
      switchMap(({ quoteId }) => {
        return this.quotesService.release(quoteId).pipe(
          map((quote) => QuotesApiActions.releasedSuccess({ quote })),
          catchError((error) => of(QuotesApiActions.releasedFailure({ error })))
        );
      })
    )
  );

  completeQuote$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        QuoteDetailsActions.quoteCompleted,
        EnquiryOverviewActions.policyMarkedAsBound
      ),
      map(({ quote }) => quote.id),
      switchMap((quoteId) => {
        return this.quotesService.complete([quoteId]).pipe(
          map((quotes) => QuotesApiActions.completedSuccess({ quotes })),
          catchError((error) =>
            of(QuotesApiActions.completedFailure({ error }))
          )
        );
      })
    )
  );

  completeQuotes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(QuoteOverviewActions.continueClicked),
      map(({ quotes }) =>
        _filter(
          quotes,
          (quote) => quote.status === QUOTE_STATUSES.BrokerApproved
        )
      ),
      filter((quotes) => !!quotes.length),
      switchMap((quotes) => {
        return this.quotesService.complete(quotes.map(({ id }) => id)).pipe(
          map((quotes) => QuotesApiActions.completedSuccess({ quotes })),
          catchError((error) =>
            of(QuotesApiActions.completedFailure({ error }))
          )
        );
      })
    )
  );

  assignLeadBrokerToQuote$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        DashboardBrokerActions.brokerAssignedToQuote,
        ClientEnquiriesActions.brokerAssigned
      ),
      switchMap(({ quoteId, user }) =>
        this.quotesService
          .assignLeadBroker(quoteId, user ? user.userId : null)
          .pipe(
            map(() => QuotesApiActions.brokerAssignedSuccess()),
            catchError((error) =>
              of(QuotesApiActions.brokerAssignedFailure({ error }))
            )
          )
      )
    )
  );

  updateQuoteStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EnquiryOverviewActions.quoteStatusUpdated),
      switchMap(({ update }) =>
        this.quotesService.updateStatus(update).pipe(
          map((quote) => QuotesApiActions.updateStatusSuccess({ quote })),
          catchError((error) =>
            of(QuotesApiActions.updateStatusFailure({ error }))
          )
        )
      )
    )
  );

  handleHealthEvaluation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EndorsementsSharedActions.endorsementDialogConfirmed),
      // filter(({ endorsement }) => endorsement.accepted),
      concatLatestFrom(() =>
        this.store.select(selectRoutedQuote).pipe(filter(Boolean))
      ),
      switchMap(([{ endorsement }, quote]) =>
        this.quotesService
          .updateStatus({
            portalId: quote.portalId,
            quoteId: quote.id,
            status: QUOTE_STATUSES.AwaitingPayment,
            additionalData: endorsement,
          })
          .pipe(
            map((quote) =>
              QuotesApiActions.endorsementSubmittedSuccess({ quote })
            ),
            catchError((error) =>
              of(QuotesApiActions.endorsementSubmittedFailure({ error }))
            )
          )
      )
    )
  );

  loadQuoteAndRedirectToEnquiries = createEffect(
    () =>
      this.actions$.pipe(
        ofType(PolicyDetailsActions.viewEnquiriesClicked),
        concatLatestFrom(() =>
          this.store
            .select(PoliciesSelectors.selectRoutedPolicy)
            .pipe(filter(Boolean))
        ),
        switchMap(([, policy]) =>
          this.quotesService
            .getQuotesByClientId(policy.policyClients[0].id)
            .pipe(
              tap((quotes) => {
                const quote = findLast(
                  quotes,
                  (quote) =>
                    policy.classOfBusinessId === quote.classOfBusinessId &&
                    quote.type === QUOTE_TYPES.MTA
                );

                if (!quote) {
                  return;
                }

                this.router.navigate(['/enquiries', quote.id]);
              }),
              takeUntil(this.actions$.pipe(ofType(ROUTER_NAVIGATED)))
            )
        )
      ),
    { dispatch: false }
  );

  updateChecklist$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EnquiryOverviewActions.checklistChanged),
      concatLatestFrom(() => this.store.select(selectRoutedQuote)),
      filter(([, quote]) => !isNil(quote)),
      switchMap(([{ quoteId, checklist }, quoteToUpdate]) =>
        this.quotesService.updateChecklist(quoteId, checklist).pipe(
          map((quote) => QuotesApiActions.checklistUpdatedSuccess({ quote })),
          catchError((error) =>
            of(
              QuotesApiActions.checklistUpdatedFailure({
                quote: quoteToUpdate!,
                error,
              })
            )
          )
        )
      )
    )
  );

  sendRenewalInviteConfirmed$ = createEffect(() =>
    this.createDialogConfirmationStream(
      [
        EnquiryOverviewActions.sendRenewalInvite,
        ClientEnquiriesActions.sendRenewalInvite,
        DashboardBrokerActions.sendInvite,
        ProposalStepActions.inviteSent,
      ],
      (quote) =>
        this.workflowMessagesService.getMessageFor(
          quote,
          DISPATCH_ACTIONS.SendRenewalInvite
        )
    ).pipe(
      map(({ quote }) =>
        LocalActions.sendRenewalInviteDialogConfirmed({ quote })
      )
    )
  );

  sendRenewalInvite$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LocalActions.sendRenewalInviteDialogConfirmed),
      switchMap(({ quote }) =>
        this.quotesService2.sendRenewalInvite(quote.id).pipe(
          map(({ responses, quote }) =>
            QuotesApiActions.sendRenewalInviteSuccess({ responses, quote })
          ),
          catchError((error) =>
            of(QuotesApiActions.sendRenewalInviteFailure({ error }))
          )
        )
      )
    )
  );

  // V2

  applyAdjustment$ = createEffect(() =>
    this.actions$.pipe(ofType(EnquiryOverviewActions.adjustmentApplied)).pipe(
      switchMap(({ adjustment, quote }) =>
        this.quotesService2.updateAdjustment(quote.id, adjustment).pipe(
          switchMap(({ quote }) => {
            this.store.dispatch(
              QuotesApiActions.adjustmentsAppliedSuccess({ quote })
            );

            return this.quotesService2.sendRenewalInvite(quote.id).pipe(
              map(({ responses, quote }) =>
                QuotesApiActions.sendRenewalInviteSuccess({
                  responses,
                  quote,
                })
              ),
              catchError((error) =>
                of(QuotesApiActions.sendRenewalInviteFailure({ error }))
              )
            );
          }),
          catchError((error) =>
            of(QuotesApiActions.adjustmentsAppliedFailure({ error }))
          )
        )
      )
    )
  );

  // updateProposalDialogConfirmed$ = createEffect(
  //   () =>
  //     this.actions$.pipe(
  //       ofType(ClientEnquiriesActions.updateProposalClicked),
  //       concatLatestFrom(() => ProposalConfigSelectors.selectAll),
  //       switchMap(() =>
  //         this.dialog
  //           .open<FormDialogComponent, FormDialogData, FormDialogCloseResult>(
  //             FormDialogComponent,
  //             {
  //               data: {
  //                 title: 'Update Proposal',
  //                 fields: [
  //                   {
  //                     fieldGroupClassName: 'vstack gap-1',
  //                     fieldGroup: [
  //                       {
  //                         className: 'mb-2',
  //                         template:
  //                           'To update your proposal please choose the relevant proposal form you wish to include.',
  //                       },
  //                       {
  //                         key: 'proposalId',
  //                         type: 'select',
  //                         props: {
  //                           label: 'Choose proposal form',
  //                           options: [],
  //                           required: true,
  //                         },
  //                       },
  //                     ],
  //                   },
  //                 ],
  //                 mode: 'create',
  //                 model: {},
  //                 confirmLabel: 'Update proposal',
  //               },
  //             }
  //           )
  //           .afterClosed()
  //       )
  //     ),
  //   { dispatch: false }
  // );

  reconfigureQuote$ = createEffect(() =>
    merge(
      this.actions$.pipe(
        ofType(UpdateEnquiryActions.formSubmitted),
        concatLatestFrom(() => [
          this.store.select(selectRoutedQuote).pipe(filter(Boolean)),
          this.store.select(PortalConfigSelectors.selectPortalConfigDefinition),
        ]),
        map(([{ enquiryConfiguration }, quote, portalConfigDef]) => {
          const partnerConfig = portalConfigDef[quote.portalId];
          const usesLegacyQuotesApi =
            !!partnerConfig?.other?.usesLegacyQuotesApi;
          return {
            enquiryConfiguration: {
              ...enquiryConfiguration,
              quoteId: quote.id,
            },
            usesLegacyQuotesApi,
          };
        }),
        filter(({ usesLegacyQuotesApi }) => !usesLegacyQuotesApi)
      ),
      this.actions$.pipe(
        ofType(ClientEnquiriesActions.updateProposalType),
        map(({ quote, subType }) => {
          const enquiryConfiguration: EnquiryConfigUpdate = {
            quoteId: quote.id,
            selectedProducts: quote.proposal
              ? _map(quote.proposal.selectedProducts, (cob) => ({
                  id: cob,
                  inceptionDate: quote.policyStartDate,
                  expiryDate: quote.policyEndDate,
                  renewalDate: quote.policyRenewalDate,
                  subtype: subType.toString(),
                }))
              : [],
          };

          return { enquiryConfiguration };
        })
      )
    ).pipe(
      switchMap(({ enquiryConfiguration }) =>
        this.quotesService2
          .reconfigure(enquiryConfiguration.quoteId, enquiryConfiguration)
          .pipe(
            map((res) => QuotesApiActions.reconfigured2Success(res)),
            catchError((error) =>
              of(QuotesApiActions.reconfigured2Failure({ error }))
            )
          )
      )
    )
  );

  saveProposal$ = createEffect(() =>
    this.createProposalSaveStream(
      ProposalStepActions.saveProposal,
      (res) => [QuotesApiActions.proposalUpdatedSuccess(res)],
      (proposal, error) =>
        of(
          LocalActions.proposalBackupSet({ proposal }),
          QuotesApiActions.proposalUpdatedFailure({ error })
        )
    )
  );

  autoSaveProposal$ = createEffect(() =>
    this.createProposalSaveStream(
      ProposalStepActions.autoSaveProposal,
      (res) => [QuotesApiActions.proposalAutoSavedSuccess(res)],
      (proposal, error) =>
        of(
          LocalActions.proposalBackupSet({ proposal }),
          QuotesApiActions.proposalAutoSavedFailure({ error })
        )
    )
  );

  submitProposalStep$ = createEffect(() =>
    this.createProposalSaveStream(
      ProposalStepActions.submitStep,
      (res) => [
        QuotesApiActions.proposalUpdatedSuccess(res),
        QuotesApiActions.proposalStepSubmitSuccess({
          proposal: res.quote.proposal as Proposal,
        }),
      ],
      (proposal, error) =>
        of(
          LocalActions.proposalBackupSet({ proposal }),
          QuotesApiActions.proposalUpdatedFailure({ error }),
          QuotesApiActions.proposalStepSubmitSuccess({ proposal })
        )
    )
  );

  scheduleProposalBackupSave$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProposalStepActions.init, ProposalStepActions.connectionRestored),
      switchMap(() =>
        this.store.select(SharedQuotesSelectors.selectEntity).pipe(
          filter(
            (quote) => !!quote && ProposalBackupService.has(quote.propFormId)
          ),
          map((quote) =>
            LocalActions.proposalBackupSaved({
              proposal: ProposalBackupService.get(
                quote!.propFormId
              ) as Proposal,
            })
          )
        )
      )
    )
  );

  saveProposalBackup$ = createEffect(() =>
    this.createProposalSaveStream(
      LocalActions.proposalBackupSaved,
      (res) => [QuotesApiActions.proposalBackupSavedSuccess(res)],
      (proposal, error) =>
        of(QuotesApiActions.proposalBackupSavedFailure({ error }))
    )
  );

  setProposalBackup$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(LocalActions.proposalBackupSet),
        tap(({ proposal }) => {
          ProposalBackupService.set(proposal);
        })
      ),
    { dispatch: false }
  );

  deleteProposalBackup$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(LocalActions.proposalBackupDeleted),
        tap(({ proposalId }) => {
          ProposalBackupService.delete(proposalId);
        })
      ),
    { dispatch: false }
  );

  submitProposalConfirmed$ = createEffect(() =>
    this.createDialogConfirmationStream(
      ProposalStepActions.submitProposalConfirmed as any,
      (quote) =>
        this.workflowMessagesService.getMessageFor(
          quote,
          DISPATCH_ACTIONS.ProposalSubmit
        )
    ).pipe(
      map(({ proposal }) => LocalActions.submitProposalConfirmed({ proposal }))
    )
  );

  submitProposal$ = createEffect(() =>
    merge(
      this.actions$.pipe(
        ofType(
          ProposalStepActions.submitProposal,
          LocalActions.submitProposalConfirmed
        ),
        concatLatestFrom(({ proposal }) => [
          this.proposalQuote(proposal),
          this.isRoutedProposalUsingLegacyQuotesApi(),
        ]),
        map(([{ proposal }, quote, usesLegacyQuotesApi]) => ({
          proposal,
          quote,
          usesLegacyQuotesApi,
        })),
        filter(({ usesLegacyQuotesApi }) => !usesLegacyQuotesApi)
      ),
      this.actions$.pipe(
        ofType(EnquiryOverviewActions.proposalSubmitted),
        map(({ quote }) => ({ quote, proposal: quote.proposal }))
      )
    ).pipe(
      map(({ quote, proposal }) => ({
        quote,
        proposal: proposal
          ? { proposalId: proposal.id, ...proposal }
          : undefined,
      })),
      switchMap(({ proposal, quote }) =>
        this.quotesService2.proposalSubmit(quote.id, proposal).pipe(
          map((res) => QuotesApiActions.proposalSubmittedSuccess(res)),
          catchError((error) =>
            of(QuotesApiActions.proposalSubmittedFailure({ error, proposal }))
          )
        )
      )
    )
  );

  returnDialogConfirmed$ = createEffect(() =>
    this.createDialogConfirmationStream(
      [
        EnquiryOverviewActions.proposalReturned,
        ProposalStepActions.proposalReturned,
        QuoteDetailsActions.changesRequested,
        ClientEnquiriesActions.proposalReturned,
        DashboardBrokerActions.proposalReturned,
      ],
      (quote) =>
        this.workflowMessagesService.getMessageFor(
          quote,
          DISPATCH_ACTIONS.Return
        )
    ).pipe(map(({ quote }) => LocalActions.returnDialogConfirmed({ quote })))
  );

  return$ = createEffect(() =>
    this.actions$.pipe(ofType(LocalActions.returnDialogConfirmed)).pipe(
      switchMap(({ quote }) =>
        this.quotesService2.return(quote.id).pipe(
          map(({ responses, quote }) =>
            QuotesApiActions.proposalReturnedSuccess({ responses, quote })
          ),
          catchError((error) =>
            of(QuotesApiActions.proposalReturnedFailure({ error }))
          )
        )
      )
    )
  );

  holdDialogConfirmed$ = createEffect(() =>
    this.createDialogConfirmationStream(
      EnquiryOverviewActions.holdQuote,
      (quote) =>
        this.workflowMessagesService.getMessageFor(quote, DISPATCH_ACTIONS.Hold)
    ).pipe(map(({ quote }) => LocalActions.holdDialogConfirmed({ quote })))
  );

  hold$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LocalActions.holdDialogConfirmed),
      switchMap(({ quote }) =>
        this.quotesService2.hold(quote.id).pipe(
          map(({ responses, quote }) =>
            QuotesApiActions.holdSuccess({ responses, quote })
          ),
          catchError((error) => of(QuotesApiActions.holdFailure({ error })))
        )
      )
    )
  );

  sendToClientDialogConfirmed$ = createEffect(() =>
    this.createDialogConfirmationStream(
      [
        EnquiryOverviewActions.sendToClient,
        QuoteDetailsActions.quoteApproved,
        QuoteOptionsActions.quoteSentToClient,
        QuoteAddonsActions.quoteSentToClient,
      ],
      (quote) =>
        this.workflowMessagesService.getMessageFor(quote, DISPATCH_ACTIONS.Send)
    ).pipe(
      map(({ quote }) => LocalActions.sendToClientDialogConfirmed({ quote }))
    )
  );

  sendToClient$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LocalActions.sendToClientDialogConfirmed),
      switchMap(({ quote }) =>
        this.quotesService2.send(quote.id).pipe(
          map(({ responses, quote }) =>
            QuotesApiActions.sendToClientSuccess({ responses, quote })
          ),
          catchError((error) =>
            of(QuotesApiActions.sendToClientFailure({ error }))
          )
        )
      )
    )
  );

  requoted$ = createEffect(() =>
    this.actions$.pipe(
      ofType(QuoteDetailsActions.reQuoted),
      switchMap(({ quote }) =>
        this.quotesService2.requote(quote.id).pipe(
          map(({ responses, quote }) =>
            QuotesApiActions.requotedSuccess({ responses, quote })
          ),
          catchError((error) => of(QuotesApiActions.requotedFailure({ error })))
        )
      )
    )
  );

  tncConfirmed$ = createEffect(() =>
    this.createDialogConfirmationStream(
      [QuoteOptionsActions.quoteAccepted, QuoteAddonsActions.quoteAccepted],
      (quote) => this.workflowMessagesService.getMessageFor(quote, 'TNC')
    ).pipe(map(({ quote }) => LocalActions.tncConfirmed({ quote })))
  );

  acceptConfirmed$ = createEffect(() =>
    this.createDialogConfirmationStream(LocalActions.tncConfirmed, (quote) =>
      this.workflowMessagesService.getMessageFor(quote, DISPATCH_ACTIONS.Accept)
    ).pipe(map(({ quote }) => LocalActions.acceptQuoteConfirmed({ quote })))
  );

  accept$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        EnquiryOverviewActions.acceptQuote,
        LocalActions.acceptQuoteConfirmed
      ),
      switchMap(({ quote }) =>
        this.quotesService2.accept(quote.id).pipe(
          map(({ responses, quote }) =>
            QuotesApiActions.acceptSuccess({ responses, quote })
          ),
          catchError((error) => of(QuotesApiActions.acceptFailure({ error })))
        )
      )
    )
  );

  invoice$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EnquiryOverviewActions.invoice),
      switchMap(({ quote }) =>
        this.quotesService2.invoice(quote.id).pipe(
          map(({ responses, quote }) =>
            QuotesApiActions.invoiceSuccess({ responses, quote })
          ),
          catchError((error) => of(QuotesApiActions.invoiceFailure({ error })))
        )
      )
    )
  );

  paymentReceivedDialogConfirmed$ = createEffect(() =>
    this.createDialogConfirmationStream(
      EnquiryOverviewActions.paymentReceived,
      (quote) =>
        this.workflowMessagesService.getMessageFor(
          quote,
          DISPATCH_ACTIONS.PaymentReceived
        )
    ).pipe(
      map(({ quote }) => LocalActions.paymentReceivedDialogConfirmed({ quote }))
    )
  );

  paymentReceived$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LocalActions.paymentReceivedDialogConfirmed),
      switchMap(({ quote }) =>
        this.quotesService2.paymentReceived(quote.id).pipe(
          map(({ responses, quote }) =>
            QuotesApiActions.paymentReceivedSuccess({ responses, quote })
          ),
          catchError((error) =>
            of(QuotesApiActions.paymentReceivedFailure({ error }))
          )
        )
      )
    )
  );

  completeDialogConfirmed$ = createEffect(() =>
    this.createDialogConfirmationStream(
      EnquiryOverviewActions.complete,
      (quote) =>
        this.workflowMessagesService.getMessageFor(
          quote,
          DISPATCH_ACTIONS.Complete
        )
    ).pipe(map(({ quote }) => LocalActions.completeDialogConfirmed({ quote })))
  );

  complete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LocalActions.completeDialogConfirmed),
      switchMap(({ quote }) =>
        this.quotesService2.complete(quote.id).pipe(
          map(({ responses, quote }) =>
            QuotesApiActions.completeSuccess({ responses, quote })
          ),
          catchError((error) => of(QuotesApiActions.completeFailure({ error })))
        )
      )
    )
  );

  quoteRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EnquiryOverviewActions.quoteRequested),
      switchMap(({ quote }) =>
        this.quotesService2.request(quote.id).pipe(
          map(({ responses, quote }) =>
            QuotesApiActions.quoteRequestedSuccess({ responses, quote })
          ),
          catchError((error) =>
            of(QuotesApiActions.quoteRequestedFailure({ error }))
          )
        )
      )
    )
  );

  quoteReady$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EnquiryOverviewActions.quoteReady),
      switchMap(({ quote }) =>
        this.quotesService2.ready(quote.id).pipe(
          map(({ responses, quote }) =>
            QuotesApiActions.quoteReadySuccess({ responses, quote })
          ),
          catchError((error) =>
            of(QuotesApiActions.quoteReadyFailure({ error }))
          )
        )
      )
    )
  );

  notTakenUpConfirmed$ = createEffect(() =>
    this.createDialogConfirmationStream(
      EnquiryOverviewActions.notTakenUp,
      (quote) =>
        this.workflowMessagesService.getMessageFor(
          quote,
          DISPATCH_ACTIONS.NotTakenUp
        )
    ).pipe(map(({ quote }) => LocalActions.notTakenUpConfirmed({ quote })))
  );

  notTakenUp$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LocalActions.notTakenUpConfirmed),
      switchMap(({ quote }) =>
        this.quotesService2.notTakenUp(quote.id).pipe(
          map(({ responses, quote }) =>
            QuotesApiActions.notTakenUpSuccess({ responses, quote })
          ),
          catchError((error) =>
            of(QuotesApiActions.notTakenUpFailure({ error }))
          )
        )
      )
    )
  );

  handleApiActions$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          QuotesApiActions.sendRenewalInviteSuccess,
          QuotesApiActions.proposalUpdatedSuccess,
          QuotesApiActions.proposalSubmittedSuccess,
          QuotesApiActions.holdSuccess,
          QuotesApiActions.proposalReturnedSuccess,
          QuotesApiActions.sendToClientSuccess,
          QuotesApiActions.acceptSuccess,
          QuotesApiActions.invoiceSuccess,
          QuotesApiActions.paymentReceivedSuccess,
          QuotesApiActions.completeSuccess
        ),
        map(({ type, responses, quote }) => ({
          actionType: type,
          responseActions: filterRegisteredApiActions(responses),
          quote,
        })),
        filter(({ responseActions }) => !isEmpty(responseActions)),
        map(({ actionType, responseActions, quote }) => {
          responseActions.forEach((action) => {
            switch (action.type) {
              case QUOTE_RESPONSE_ACTIONS.NotificationEmailSent: {
                const message = MESSAGES2[actionType];

                if (message) {
                  this.feedbackService.subtleMessage(message.text, {
                    type: message.type,
                  });
                }
                return;
              }

              case QUOTE_RESPONSE_ACTIONS.RedirectFormSuccess:
                return this.router.navigate(['/proposals/success']);

              case QUOTE_RESPONSE_ACTIONS.RedirectFormReferred:
                return this.router.navigate(['/proposals/referred']);

              case QUOTE_RESPONSE_ACTIONS.RedirectEnquiryOverview:
                return this.router.navigate([`/enquiries/${quote.id}`]);

              case QUOTE_RESPONSE_ACTIONS.RedirectFormReferredDueToLateSubmission:
                return this.router.navigate(['/proposals/referred'], {
                  queryParams: { 'late-submission': 1 },
                });

              case QUOTE_RESPONSE_ACTIONS.RedirectEnquiryConfiguration:
                return this.router.navigate([
                  `/enquiries/${quote.id}/configuration`,
                ]);

              case QUOTE_RESPONSE_ACTIONS.RedirectQuoteOptions:
                return this.router.navigate([`/quotes/${quote.id}/options`]);

              case QUOTE_RESPONSE_ACTIONS.RedirectQuoteSummary:
                return this.router.navigate([`/quotes/${quote.id}/summary`]);
            }
          });
        })
      ),

    { dispatch: false }
  );

  // Navigation / Redirects

  navigateToDocuments$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(QuoteOverviewActions.continueClicked),
        map(({ quotes }) =>
          _filter(quotes, (quote) =>
            includes(
              [QUOTE_STATUSES.AwaitingPayment, QUOTE_STATUSES.Complete],
              quote.status
            )
          )
        ),
        filter((quotes) => quotes.length === 1),
        tap((quotes) => {
          this.router.navigate(['/policies', quotes[0].policyId], {
            fragment: 'documents',
          });
        })
      ),
    { dispatch: false }
  );

  redirectToQuoteEnquiries$ = createEffect(
    () =>
      merge(
        this.actions$.pipe(
          ofType(
            QuotesApiActions.releasedSuccess,
            QuotesApiActions.updateStatusSuccess,
            QuotesApiActions.quoteApprovedSuccess,
            QuotesApiActions.endorsementSubmittedSuccess
          ),
          filter(({ quote }) => quote.status !== QUOTE_STATUSES.Defunct),
          map(({ quote }) => quote)
        ),
        this.actions$.pipe(
          ofType(QuotesApiActions.initialisedSuccess),
          map(({ quotes }) =>
            reject(quotes, { status: QUOTE_STATUSES.Defunct })
          ),
          filter((quotes) => quotes.length > 0),
          map(head<Quote>),
          filter(Boolean),
          filter((quote) => !this.redirectToPropOnInit(quote.portalId))
        )
      ).pipe(
        tap((quote: Quote) => {
          const configurationRequired: boolean = includes(
            [Partners.ACCA],
            quote.portalId
          );

          this.router.navigate(
            compact([
              '/',
              'enquiries',
              quote.id,
              configurationRequired && 'configuration',
            ])
          );
        })
      ),

    { dispatch: false }
  );

  redirectToPropForm$ = createEffect(
    () =>
      merge(
        this.actions$.pipe(
          ofType(QuotesApiActions.initialisedSuccess),
          map(({ quotes }) =>
            reject(quotes, { status: QUOTE_STATUSES.Defunct })
          ),
          filter((quotes) => quotes.length > 0),
          map(head<Quote>)
        )
      ).pipe(
        filter(Boolean),
        filter((quote) => this.redirectToPropOnInit(quote.portalId)),
        tap((quote) => {
          this.router.navigate(getProposalUrl(quote));
        })
      ),

    { dispatch: false }
  );

  redirectHome$ = createEffect(
    () =>
      merge(
        this.actions$.pipe(
          ofType(QuotesApiActions.updateStatusSuccess),
          filter(({ quote }) => quote.status === QUOTE_STATUSES.Defunct)
        )
      ).pipe(
        tap(() => {
          this.router.navigate(['/']);
        })
      ),

    { dispatch: false }
  );

  navigateToInvoiceSummary$ = createEffect(
    () =>
      merge(
        this.actions$.pipe(
          ofType(QuoteOverviewActions.continueClicked),
          filter(({ quotes }) => !quotes.length)
        ),
        this.actions$.pipe(
          ofType(QuotesApiActions.completedSuccess),
          concatLatestFrom(() => this.store.select(AuthSelectors.getRole)),
          filter(([, role]) => role === Role.Contact)
        )
      ).pipe(
        tap(() => {
          this.router.navigate(['/invoice-summary'], {
            queryParamsHandling: 'preserve',
          });
        })
      ),
    { dispatch: false }
  );

  // Messages

  /**
   * @deprecated - use WorkflowFeedbackMessagesService instead
   *  */
  feedbackMessages$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          QuotesApiActions.endorsementSubmittedSuccess,
          QuotesApiActions.releasedSuccess,
          QuotesApiActions.brokerAssignedSuccess,
          QuotesApiActions.completedSuccess,
          QuotesApiActions.tierSelectedSuccess,
          QuotesApiActions.tierAdjustedSuccess,
          QuotesApiActions.adjustmentsAppliedSuccess,
          QuotesApiActions.addonAdjustedSuccess,
          QuotesApiActions.updateStatusSuccess,
          QuotesApiActions.holdSuccess,
          QuotesApiActions.requotedSuccess,
          QuotesApiActions.acceptSuccess,
          QuotesApiActions.invoiceSuccess,
          QuotesApiActions.reconfigured2Success,
          QuotesApiActions.proposalReturnedSuccess
        ),
        concatLatestFrom(() => this.store.select(selectRoutedQuote)),
        // suppress for partners that are using new workflow feedback messages service
        filter(([, quote]) => quote?.portalId !== Partners.LSOS_ADDON),
        tap(([{ type: actionType }]) => {
          const { text, type } = MESSAGES2[actionType];

          this.feedbackService.subtleMessage(text, {
            type,
          });
        })
      ),
    { dispatch: false }
  );

  approveQuoteSuccessMessage$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(QuotesApiActions.quoteApprovedSuccess),
        filter(({ quote }) => quote.status === QUOTE_STATUSES.BrokerApproved),
        tap(() => {
          this.feedbackService.subtleMessage(MESSAGES.approvedSuccess, {
            type: 'success',
          });
        })
      ),
    { dispatch: false }
  );

  healthEvaluationRequiredMessage$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(QuotesApiActions.quoteApprovedSuccess),
        filter(
          ({ quote }) =>
            quote.status === QUOTE_STATUSES.HealthEvaluationRequired
        ),
        tap(() => {
          this.feedbackService.subtleMessage(
            MESSAGES.healthEvaluationRequired,
            { type: 'info' }
          );
        })
      ),
    { dispatch: false }
  );

  toggleAddonSuccessMessage$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(QuotesApiActions.addonToggledSuccess),
        tap(({ addon }) => {
          this.feedbackService.subtleMessage(
            addon.enabled ? MESSAGES.addonEnabled : MESSAGES.addonDisabled,
            {
              type: 'success',
            }
          );
        })
      ),
    { dispatch: false }
  );

  genericFailureMessage$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          QuotesApiActions.addonToggledFailure,
          QuotesApiActions.addonAdjustedFailure,
          QuotesApiActions.tierSelectedFailure,
          QuotesApiActions.adjustmentsAppliedFailure,
          QuotesApiActions.tierAdjustedFailure,
          QuotesApiActions.updateStatusFailure,
          QuotesApiActions.checklistUpdatedFailure,
          QuotesApiActions.reconfigured2Failure,
          QuotesApiActions.sendRenewalInviteFailure,
          QuotesApiActions.initialisedFailure,
          QuotesApiActions.proposalSubmittedFailure
        ),
        tap(() => {
          this.feedbackService.subtleMessage(MESSAGES.genericFailure, {
            type: 'error',
          });
        })
      ),
    { dispatch: false }
  );

  handlers$ = createEffect(
    () =>
      merge(
        this.actions$.pipe(
          ofType(QuotesApiActions.checklistUpdatedSuccess),
          map(({ type, quote }) => ({
            type,
            res: { quote },
          }))
        ),
        this.actions$.pipe(
          ofType(QuotesApiActions.reconfiguredSuccess),
          map(({ type, quotes }) => ({
            type,
            res: { quotes },
          }))
        )
      ).pipe(
        map(({ type, res }) => ({
          handler: handlers[type],
          res,
        })),
        filter(({ handler }) => Boolean(handler)),
        tap(({ handler, res }) => {
          handler.redirect && this.router.navigate(handler.redirect(res).path);
          handler.message &&
            this.feedbackService.subtleMessage(handler.message.text, {
              type: handler.message.type,
            });
        })
      ),
    { dispatch: false }
  );

  // Deprecated Effects

  /** @deprecated  */
  legacyLoadQuotes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DashboardBrokerLsosActions.init),
      switchMap(() =>
        this.quotesService.legacyGetQuotes().pipe(
          map((quotes) => QuotesApiActions.loadQuotesSuccess({ quotes })),
          catchError((error) =>
            of(QuotesApiActions.loadQuotesFailure({ error }))
          )
        )
      )
    )
  );

  /** @deprecated  */
  legacyNavigateToRenewals$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(QuotesApiActions.changesRequestedSuccess),
        // #TODO remove when patch api corrected
        concatLatestFrom(() => this.store.select(selectLatestQuote)),
        filter(([, quote]) => !isEmpty(quote)),
        tap(([, quote]) => {
          this.router.navigate(['/lsos', quote?.clientId, 'renewals']);
        })
      ),
    { dispatch: false }
  );

  /** @deprecated: only used for legacy lsos */
  legacyRenewalsApplicationLoadQuote$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LsosRenewalsApplicationActions.loadQuotes),

      switchMap(({ clientId }) => {
        if (!clientId) {
          return throwError(() => new Error('No client Id provided'));
        }

        return this.quotesService2.getQuotesByClientId(clientId).pipe(
          map((res) => {
            if (!res.length) {
              return LsosRenewalsApplicationActions.loadEmptyQuotesSuccess();
            }
            return LsosRenewalsApplicationActions.loadQuotesSuccess({
              quotes: res,
            });
          })
        );
      }),
      catchError((error) => {
        console.error('Error', error);
        return of(LsosRenewalsApplicationActions.loadQuotesFailure({ error }));
      })
    )
  );

  /** @deprecated: only used for legacy lsos */
  legacyShowNotificationForLiveQuotes$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(QuotesApiActions.loadClientQuotesWithLiveCheckSuccess),
        map(({ quotes }) =>
          find(quotes, (quote) => quote.status !== QUOTE_STATUSES.Complete)
        ),
        filter((quote) => !isNil(quote) && quote.portalId === Partners.LSOS),
        concatLatestFrom(() => this.store.select(AuthSelectors.getRole)),
        tap(([quote, role]) => {
          if (quote && role === Role.Contact) {
            this.renewalsNotification.openForContact(quote);
          }
          if (quote && role !== Role.Contact) {
            this.renewalsNotification.openForBroker(quote);
          }
        })
      ),
    { dispatch: false }
  );

  /** @deprecated: only used for legacy lsos */
  legacyLoadClientQuoteFromRouteParam$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LsosRenewalsOverviewActions.init),
      concatLatestFrom(() => this.store.select(selectRouteParam('clientId'))),
      map(([, clientId]) => {
        if (!clientId) {
          throw Error('No client Id');
        }
        return { clientId: toInteger(clientId) };
      }),
      switchMap(({ clientId }) =>
        this.quotesService2.getQuotesByClientId(clientId).pipe(
          map((res) => {
            if (!res.length) {
              return LsosRenewalsOverviewActions.loadEmptyQuotesSuccess();
            }
            return LsosRenewalsOverviewActions.loadQuotesSuccess({
              quotes: res,
            });
          }),
          catchError((error) => {
            return of(LsosRenewalsOverviewActions.loadQuotesFailure({ error }));
          })
        )
      ),
      catchError((error) => {
        return of(LsosRenewalsOverviewActions.loadQuotesFailure({ error }));
      })
    )
  );

  /** @deprecated: only used for legacy lsos */
  legacyLoadQuotesForContactDashboard$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PoliciesApiActions.loadPoliceiesForContactDashboardSuccess),
      map(({ policies }) =>
        find(policies, (p) => p.classOfBusinessId === PolicyCobMap.LSOS)
      ),
      switchMap((policy) => {
        const clientId = policy ? policy.policyClients[0].id : null;

        if (!clientId) {
          return of(
            QuotesApiActions.loadQuotesForContactDashboardSuccess({
              quotes: [],
            })
          );
        }

        return this.quotesService2.getQuotesByClientId(clientId).pipe(
          map((res) => {
            return QuotesApiActions.loadQuotesForContactDashboardSuccess({
              quotes: res,
            });
          })
        );
      }),
      catchError((error) => {
        console.error('Error', error);
        return of(
          QuotesApiActions.loadQuotesForContactDashboardFailure({ error })
        );
      })
    )
  );

  constructor(
    private store: Store,
    private actions$: Actions,
    private quotesService: QuotesService,
    private quotesService2: Quotes2Service,
    private feedbackService: FeedbackService,
    private renewalsNotification: RenewalNotificationsService,
    private router: Router,
    private dialog: MatDialog,
    private workflowMessagesService: WorkflowMessagesService,
    private dateFnsHelperService: DateFnsHelpersService
  ) {}

  private proposalQuote(proposal: Proposal) {
    return this.store.select(SharedQuotesSelectors.selectAllQuotes).pipe(
      map((quotes) => find(quotes, { propFormId: proposal.id })),
      filter(Boolean)
    );
  }

  private isRoutedProposalUsingLegacyQuotesApi() {
    return combineLatest([
      this.store
        .select(selectRouteParam('portalId'))
        .pipe(filter(Boolean), map(Number)),
      this.store.select(PortalConfigSelectors.selectPortalConfigDefinition),
    ]).pipe(
      map(([portalId, portalConfigDef]) => {
        const partnerConfig = portalConfigDef[portalId];
        const usesLegacyQuotesApi = !!partnerConfig?.other?.usesLegacyQuotesApi;
        return usesLegacyQuotesApi;
      })
    );
  }

  private createProposalSaveStream(
    actions: SaveProposalActionCreator | SaveProposalActionCreator[],
    onSuccess: (response: PatchQuoteResponse) => Action[],
    onFailure: (proposal: Proposal, error: any) => Observable<any>
  ): Observable<any> {
    return this.actions$.pipe(
      ofType(...castArray(actions)),
      concatLatestFrom(() => [
        this.isRoutedProposalUsingLegacyQuotesApi(),
        this.store.select(AuthSelectors.getRole),
      ]),
      map(([{ proposal }, usesLegacyQuotesApi, role]) => ({
        proposal,
        usesLegacyQuotesApi,
        role,
      })),
      filter(({ usesLegacyQuotesApi }) => !usesLegacyQuotesApi),
      map(({ proposal, role }) => {
        const setContactInitialized =
          role === Role.Contact && !proposal.formData.clientInitialized;

        return setContactInitialized
          ? defaultsDeep({ formData: { clientInitialized: true } }, proposal)
          : proposal;
      }),
      concatLatestFrom((proposal) => this.proposalQuote(proposal)),
      switchMap(([proposal, quote]) =>
        this.quotesService2
          .proposalUpdate(quote.id, {
            proposalId: proposal.id,
            ...proposal,
          })
          .pipe(
            switchMap(onSuccess),
            tap(() => {
              if (Connection.isOnline) {
                this.store.dispatch(
                  LocalActions.proposalBackupDeleted({
                    proposalId: proposal.id,
                  })
                );
              }
            }),
            catchError(onFailure.bind(this, proposal))
          )
      )
    );
  }

  private createDialogConfirmationStream(
    actions: PatchQuoteActionCreator | PatchQuoteActionCreator[],
    dataCreator: (quote: Quote) => ConfirmDialogData
  ): Observable<PatchQuoteActionCreatorProps> {
    const actionsArray = Array.isArray(actions) ? actions : [actions];
    return this.actions$.pipe(
      ofType(...actionsArray),
      // TODO: The usesLegacyQuotesApi can be removed once all legacy verticals have been migrated
      concatLatestFrom(() =>
        this.store.select(PortalConfigSelectors.selectPortalConfigDefinition)
      ),
      map(([props, portalConfigDef]) => ({
        ...props,
        usesLegacyQuotesApi:
          !!portalConfigDef[props.quote.portalId]?.other?.usesLegacyQuotesApi,
      })),
      filter(({ usesLegacyQuotesApi }) => !usesLegacyQuotesApi),
      switchMap((props: PatchQuoteActionCreatorProps) => {
        if (this.isConfirmationDialogException(props)) {
          return of(props);
        }

        return this.dialog
          .open<ConfirmationDialogComponent, ConfirmDialogData>(
            ConfirmationDialogComponent,
            {
              data: dataCreator(props.quote),
            }
          )
          .afterClosed()
          .pipe(
            filter(Boolean),
            map(() => props)
          );
      })
    );
  }

  private newEnquiryConfirmation$(
    enquiryConfig: EnquiryConfig,
    client: Client,
    config?: ApplicationConfig
  ) {
    if (client.portalId !== Partners.ENW || !config) {
      return of(enquiryConfig);
    }

    const content =
      enquiryConfig.type === QUOTE_TYPES.NewBusiness
        ? $localize`By proceeding with creating this new quote <strong>${client.clientData.insuredName}</strong> will be emailed a new business invite for <strong>${config.label}</strong> proposal form.`
        : $localize`By proceeding with creating this new renewal <strong>${client.clientData.insuredName}</strong> will be emailed a renewal invite for <strong>${config.label}</strong> proposal form.`;

    return this.dialog
      .open<ConfirmationDialogComponent, ConfirmDialogData>(
        ConfirmationDialogComponent,
        {
          data: {
            title: $localize`Are you sure?`,
            content,
            confirmButtonText: $localize`Yes, continue`,
            cancelButtonText: $localize`Cancel`,
          },
        }
      )
      .afterClosed()
      .pipe(
        map((confirmed: boolean) => {
          if (!confirmed) {
            this.store.dispatch(NewEnquiryActions.formSubmitCancelled());
          }

          return confirmed && enquiryConfig;
        }),
        filter(Boolean)
      );
  }

  private redirectToPropOnInit(portalId: number) {
    return includes([Partners.PNC, Partners.NORWAY], portalId);
  }

  private redirectToQuoteConfigurationOnInit(portalId: number) {
    return includes([Partners.ACCA], portalId);
  }

  private isConfirmationDialogException(
    props: PatchQuoteActionCreatorProps
  ): boolean {
    return some(
      CONFIRMATION_DIALOG_EXCEPTIONS,
      ([partner, action]) =>
        props.quote.portalId === partner && props.type === action
    );
  }
}
